import { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Landing route for Stripe Checkout's return URLs:
 *   STRIPE_SUCCESS_URL = https://preppos.com/billing?checkout=success
 *   STRIPE_CANCEL_URL  = https://preppos.com/billing?checkout=cancelled
 *
 * There was no /billing route, so a merchant who had just paid landed on the
 * 404 page — and "Return to Home" sent them back to onboarding before the
 * webhook arrived, inviting them to pay a second time. This forwards each
 * return to the screen that handles it:
 *   success   → a first checkout (started in onboarding) goes to
 *               /app/dashboard?checkout=success, where ProtectedRoute shows the
 *               "Finalizing…" poller until the webhook writes the subscription
 *               and the welcome popup opens. A checkout started from the
 *               billing page (resubscribe) goes back to /app/billing.
 *   cancelled → nothing was charged. Clear the pending flag — otherwise a later
 *               /app visit shows "finalizing" to someone who didn't pay — and
 *               send them back to where they chose a plan.
 *
 * Where checkout started is read from the pending flag, which only onboarding
 * sets. isOnboarded alone can't tell: the webhook often lands before this page
 * loads, so a first-time payer already looks onboarded.
 */
export default function CheckoutReturn() {
  const { isOnboarded, loading } = useAuth();
  const [params] = useSearchParams();
  const outcome = params.get("checkout");
  const firstCheckout =
    sessionStorage.getItem("prep_checkout_pending") === "1" || !isOnboarded;

  useEffect(() => {
    if (outcome === "cancelled") {
      sessionStorage.removeItem("prep_checkout_pending");
      toast("Checkout cancelled — you haven't been charged.", { id: "checkout-cancelled" });
    } else if (outcome === "success" && !loading && firstCheckout) {
      // Keep the poller armed even if a login detour drops ?checkout=success
      // (Login only restores the pathname). ProtectedRoute clears it once the
      // merchant is onboarded.
      sessionStorage.setItem("prep_checkout_pending", "1");
    }
  }, [outcome, loading, firstCheckout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (outcome === "success") {
    return <Navigate to={firstCheckout ? "/app/dashboard?checkout=success" : "/app/billing"} replace />;
  }
  if (outcome === "cancelled") {
    return <Navigate to={isOnboarded ? "/app/billing" : "/onboarding/step/activate"} replace />;
  }
  return <Navigate to={isOnboarded ? "/app/billing" : "/"} replace />;
}
